import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Panel, StatusBadge, Empty, Select } from "./ui";
import { useAuth } from "@/context/AuthContext";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Trash2, Zap, Warehouse, ListChecks, FileText, ScanLine, X } from "lucide-react";

export default function Replenishment() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const siteFilter = params.get("site");
  const [reqs, setReqs] = useState([]);
  const [sites, setSites] = useState([]);
  const [products, setProducts] = useState([]);
  const [rules, setRules] = useState([]);
  const [settings, setSettings] = useState({ central_direct_fulfilment: false });
  const [form, setForm] = useState({ site_id: "", product_id: "", qty: 1 });
  const [rule, setRule] = useState({ site_id: "", product_id: "", min: 1, max: 5 });
  const [pick, setPick] = useState(null); // { req, source, units, sel }
  const [pickScan, setPickScan] = useState("");
  const [pickScanMissing, setPickScanMissing] = useState([]);
  const [pickScanInput, setPickScanInput] = useState("");
  const [pickScanFeed, setPickScanFeed] = useState([]); // [{serial, status}]

  useEffect(() => {
    if (siteFilter && reqs.length) document.querySelector('[data-testid="rep-site-filter-banner"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [siteFilter, reqs.length]);
  const shownReqs = siteFilter ? reqs.filter((r) => r.site_id === siteFilter && ["pending", "dispatched"].includes(r.status)) : reqs;
  const canRequest = ["fitment_technician", "operations_controller", "regional_manager", "system_admin"].includes(user.role);
  const canFulfil = ["regional_manager", "operations_controller", "system_admin"].includes(user.role);
  const canReceive = ["fitment_technician", "regional_manager", "operations_controller", "system_admin"].includes(user.role);
  const canRules = ["operations_controller", "regional_manager", "warehouse_manager", "system_admin"].includes(user.role);
  const canSettings = user.role === "system_admin";

  const load = useCallback(() => {
    api.get("/replenishment").then((r) => setReqs(r.data));
    api.get("/stock-rules").then((r) => setRules(r.data));
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);
  useEffect(() => {
    load();
    api.get("/locations").then((r) => setSites(r.data.filter((l) => ["fitment_centre", "technician"].includes(l.type))));
    api.get("/products").then((r) => setProducts(r.data));
  }, [load]);

  const request = async () => {
    if (!form.site_id || !form.product_id) { toast.error("Pick site and product"); return; }
    try {
      await api.post("/replenishment", { ...form, qty: Number(form.qty) });
      toast.success("Replenishment requested");
      setForm({ site_id: "", product_id: "", qty: 1 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const fulfil = async (id) => {
    try {
      const { data } = await api.post(`/replenishment/${id}/fulfil`);
      toast.success(`Dispatched from ${data.source || "regional warehouse"} — in transit, awaiting receipt`);
      load();
    }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openPick = async (req) => {
    try {
      const { data } = await api.get(`/replenishment/${req.id}/available`);
      setPick({ req, source: data.source, units: data.units, sel: [] });
      setPickScan(""); setPickScanMissing([]); setPickScanInput(""); setPickScanFeed([]);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load available units"); }
  };

  const applyPickScan = () => {
    const wanted = Array.from(new Set(pickScan.split(/[\n,\s]+/).map((s) => s.trim()).filter(Boolean)));
    if (wanted.length === 0) { toast.error("Paste or scan some serials first"); return; }
    const avail = new Set(pick.units.map((u) => u.serial));
    const matched = wanted.filter((s) => avail.has(s));
    const missing = wanted.filter((s) => !avail.has(s));
    setPickScanMissing(missing);
    if (matched.length === 0) { toast.error("None of those serials are available from the source"); return; }
    setPick((p) => ({ ...p, sel: matched }));
    toast.success(`${matched.length} matched & selected${missing.length ? ` · ${missing.length} not available here` : ""}`);
  };

  const onPickScanEnter = (e) => {
    if (e.key !== "Enter" && e.key !== "Tab") return;
    e.preventDefault();
    const s = pickScanInput.trim();
    setPickScanInput("");
    if (!s) return;
    if (!pick.units.some((u) => u.serial === s)) {
      setPickScanFeed((f) => [{ serial: s, status: "notfound" }, ...f]); return;
    }
    if (pick.sel.includes(s)) {
      setPickScanFeed((f) => [{ serial: s, status: "dup" }, ...f]); return;
    }
    if (pick.sel.length >= pick.req.qty) {
      setPickScanFeed((f) => [{ serial: s, status: "full" }, ...f]);
      toast.error(`Already have ${pick.req.qty} — remove one before adding another`);
      return;
    }
    setPick((p) => ({ ...p, sel: [...p.sel, s] }));
    setPickScanFeed((f) => [{ serial: s, status: "ok" }, ...f]);
  };

  const togglePick = (serial) => setPick((p) => ({
    ...p, sel: p.sel.includes(serial) ? p.sel.filter((x) => x !== serial) : [...p.sel, serial],
  }));

  const confirmPick = async () => {
    if (pick.sel.length !== pick.req.qty) {
      toast.error(`Select exactly ${pick.req.qty} unit(s) — ${pick.sel.length} selected`);
      return;
    }
    try {
      const { data } = await api.post(`/replenishment/${pick.req.id}/fulfil`, { serials: pick.sel });
      toast.success(`Dispatched ${data.moved.length} chosen unit(s) from ${data.source} — in transit`);
      setPick(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const dispatchNote = async (req) => {
    try {
      const res = await api.get(`/replenishment/${req.id}/dispatch-note.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `netstar_dispatch_${req.id.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not generate dispatch note"); }
  };

  const acknowledge = async (id) => {
    try {
      const { data } = await api.post(`/replenishment/${id}/acknowledge`, {});
      toast.success(`Receipt confirmed — ${data.received.length} unit(s) booked in at site`);
      load();
    }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleDirect = async (checked) => {
    try {
      const { data } = await api.put("/settings", { central_direct_fulfilment: checked });
      setSettings(data);
      toast.success(checked ? "Fulfilment now draws directly from the central bin" : "Fulfilment now routes via regional warehouses");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const saveRule = async () => {
    if (!rule.site_id || !rule.product_id) { toast.error("Pick site and product"); return; }
    try {
      await api.post("/stock-rules", { site_id: rule.site_id, product_id: rule.product_id, min: Number(rule.min), max: Number(rule.max) });
      toast.success("Stock rule saved");
      setRule({ site_id: "", product_id: "", min: 1, max: 5 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const delRule = async (id) => {
    try { await api.delete(`/stock-rules/${id}`); toast.success("Rule removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const runAutoCheck = async () => {
    try {
      const { data } = await api.post("/replenishment/auto-check");
      if (data.count === 0) toast.info("All sites are at or above their minimum — nothing to raise.");
      else toast.success(`${data.count} auto request(s) raised`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader title="Replenishment" subtitle="Tier 1: regional warehouse restocks sites & technicians. Tier 2: main bin tops up regions (via Distribution).">
        {canRules && (
          <Button data-testid="run-auto-check" onClick={runAutoCheck} className="rounded-sm bg-[#0B2545] hover:bg-[#0B2545]/90 font-head gap-1.5">
            <Zap className="h-4 w-4" /> Run auto-check now
          </Button>
        )}
      </PageHeader>

      <Panel className="mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap" data-testid="direct-fulfilment-panel">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 h-9 w-9 rounded-sm flex items-center justify-center shrink-0" style={{ background: settings.central_direct_fulfilment ? "#f0fdfa" : "#f1f5f9" }}>
              <Warehouse className="h-5 w-5" style={{ color: settings.central_direct_fulfilment ? "#00857C" : "#64748b" }} />
            </div>
            <div>
              <div className="font-head font-semibold text-sm text-[#0B2545]">Central bin direct fulfilment</div>
              <p className="text-xs text-slate-500 mt-0.5 max-w-xl">
                When ON, fulfilling a request (auto or manual) draws stock straight from the central Midrand consignment bin,
                bypassing the regional warehouses. When OFF, fulfilment pulls from the site's regional warehouse.
              </p>
              <div className="mt-1.5">
                <span data-testid="direct-fulfilment-status" className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wide"
                  style={settings.central_direct_fulfilment ? { background: "#f0fdfa", color: "#00857C" } : { background: "#f1f5f9", color: "#475569" }}>
                  {settings.central_direct_fulfilment ? "Central bin (direct)" : "Regional route"}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Switch data-testid="direct-fulfilment-toggle" checked={settings.central_direct_fulfilment}
              disabled={!canSettings} onCheckedChange={toggleDirect} />
            {!canSettings && <span className="text-[11px] text-slate-400">System Admin only</span>}
          </div>
        </div>
      </Panel>

      {canRules && (
        <Panel title="Stock Rules — min / max per site" className="mb-6">
          <div className="text-xs text-slate-400 mb-3">When a site's on-hand (units at that site) drops below Min, the system auto-raises a request to top up to Max. Triggered after installs/returns and by "Run auto-check now".</div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end mb-4">
            <div><Label className="overline text-slate-500">Site / Technician</Label><div className="mt-1"><Select testid="rule-site" value={rule.site_id} onChange={(v) => setRule({ ...rule, site_id: v })} options={sites.map((s) => ({ value: s.id, label: s.name }))} placeholder="Select site" /></div></div>
            <div><Label className="overline text-slate-500">Product</Label><div className="mt-1"><Select testid="rule-product" value={rule.product_id} onChange={(v) => setRule({ ...rule, product_id: v })} options={products.map((p) => ({ value: p.id, label: p.name }))} placeholder="Select product" /></div></div>
            <div><Label className="overline text-slate-500">Min</Label><Input data-testid="rule-min" type="number" min={0} value={rule.min} onChange={(e) => setRule({ ...rule, min: e.target.value })} className="rounded-sm mt-1" /></div>
            <div><Label className="overline text-slate-500">Max</Label><Input data-testid="rule-max" type="number" min={0} value={rule.max} onChange={(e) => setRule({ ...rule, max: e.target.value })} className="rounded-sm mt-1" /></div>
            <Button data-testid="rule-save" onClick={saveRule} className="rounded-sm bg-[#00857C] hover:bg-[#00a89d] font-head">Save rule</Button>
          </div>
          {rules.length === 0 ? <Empty>No stock rules yet. Add one above to enable auto-replenishment.</Empty> : (
            <table className="w-full text-sm" data-testid="stock-rules-table">
              <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-3">Site</th><th className="py-2 pr-3">Product</th><th className="py-2 pr-3">On-hand</th><th className="py-2 pr-3">Min</th><th className="py-2 pr-3">Max</th><th className="py-2 pr-3">Status</th><th></th></tr></thead>
              <tbody>
                {rules.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100">
                    <td className="py-2 pr-3 font-medium text-[#0B2545]">{r.site_name}</td>
                    <td className="py-2 pr-3 text-slate-600">{r.product_name}</td>
                    <td className="py-2 pr-3 font-head font-bold text-[#0B2545]">{r.onhand}</td>
                    <td className="py-2 pr-3 text-slate-500">{r.min}</td>
                    <td className="py-2 pr-3 text-slate-500">{r.max}</td>
                    <td className="py-2 pr-3">
                      {r.below_min
                        ? <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wide" style={{ background: "#fef2f2", color: "#dc2626" }}>Below min</span>
                        : <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wide" style={{ background: "#f0fdfa", color: "#00857C" }}>OK</span>}
                    </td>
                    <td className="py-2 text-right"><button data-testid={`rule-del-${r.id}`} onClick={() => delRule(r.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      )}

      {canRequest && (
        <Panel title="Raise Replenishment Request" className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div><Label className="overline text-slate-500">Site / Technician</Label><div className="mt-1"><Select testid="rep-site" value={form.site_id} onChange={(v) => setForm({ ...form, site_id: v })} options={sites.map((s) => ({ value: s.id, label: s.name }))} placeholder="Select site" /></div></div>
            <div><Label className="overline text-slate-500">Product</Label><div className="mt-1"><Select testid="rep-product" value={form.product_id} onChange={(v) => setForm({ ...form, product_id: v })} options={products.map((p) => ({ value: p.id, label: p.name }))} placeholder="Select product" /></div></div>
            <div><Label className="overline text-slate-500">Quantity</Label><Input data-testid="rep-qty" type="number" min={1} value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} className="rounded-sm mt-1" /></div>
            <Button data-testid="rep-submit" onClick={request} className="rounded-sm bg-[#00857C] hover:bg-[#00a89d] font-head">Request</Button>
          </div>
        </Panel>
      )}
      <Panel title="Requests">
        {siteFilter && (
          <div data-testid="rep-site-filter-banner" className="mb-3 flex items-center justify-between gap-3 rounded-sm border border-[#00857C]/30 bg-[#f0fdfa] px-3 py-2 text-sm">
            <span className="text-[#0B2545]">Showing open requests for <b>{sites.find((s) => s.id === siteFilter)?.name || "selected site"}</b></span>
            <button data-testid="rep-site-filter-clear" onClick={() => setParams({})} className="inline-flex items-center gap-1 text-xs text-[#00857C] hover:underline"><X className="h-3.5 w-3.5" /> Show all</button>
          </div>
        )}
        {shownReqs.length === 0 ? <Empty>No replenishment requests.</Empty> : (
          <table className="w-full text-sm" data-testid="replenishment-table">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-3">Site</th><th className="py-2 pr-3">Product</th><th className="py-2 pr-3">Qty</th><th className="py-2 pr-3">Source</th><th className="py-2 pr-3">Status</th><th className="py-2 pr-3">By</th><th></th></tr></thead>
            <tbody>
              {shownReqs.map((r) => (
                <tr key={r.id} data-testid={`rep-row-${r.id}`} className={`border-b border-slate-100 ${siteFilter && r.status === "pending" ? "bg-amber-50" : ""}`}>
                  <td className="py-2 pr-3 font-medium text-[#0B2545]">{r.site_name}</td>
                  <td className="py-2 pr-3 text-slate-600">{r.product_name}</td>
                  <td className="py-2 pr-3">{r.qty}</td>
                  <td className="py-2 pr-3">
                    {r.source === "auto"
                      ? <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wide inline-flex items-center gap-1" style={{ background: "#eef2ff", color: "#4338ca" }}><Zap className="h-2.5 w-2.5" />Auto</span>
                      : <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wide" style={{ background: "#f1f5f9", color: "#475569" }}>Manual</span>}
                  </td>
                  <td className="py-2 pr-3"><StatusBadge status={r.status} /></td>
                  <td className="py-2 pr-3 text-slate-500 text-xs">{r.created_by}</td>
                  <td className="py-2 text-right">{r.status === "pending" && canFulfil && (
                    <div className="flex items-center justify-end gap-2">
                      <Button data-testid={`pick-${r.id}`} onClick={() => openPick(r)} size="sm" variant="outline" className="rounded-sm border-slate-300 h-7 text-xs gap-1">
                        <ListChecks className="h-3.5 w-3.5" /> Choose serials
                      </Button>
                      <Button data-testid={`fulfil-${r.id}`} onClick={() => fulfil(r.id)} size="sm" className="rounded-sm bg-[#0B2545] hover:bg-[#0B2545]/90 h-7 text-xs">Auto-fulfil</Button>
                    </div>
                  )}{r.status === "dispatched" && canReceive && (
                    <Button data-testid={`acknowledge-${r.id}`} onClick={() => acknowledge(r.id)} size="sm" className="rounded-sm bg-[#ca8a04] hover:bg-[#ca8a04]/90 h-7 text-xs">Confirm receipt</Button>
                  )}{["dispatched", "received"].includes(r.status) && (
                    <Button data-testid={`dispatch-note-${r.id}`} onClick={() => dispatchNote(r)} size="sm" variant="outline" className="rounded-sm border-slate-300 h-7 text-xs gap-1 ml-2">
                      <FileText className="h-3.5 w-3.5" /> Dispatch note
                    </Button>
                  )}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Dialog open={!!pick} onOpenChange={(o) => { if (!o) setPick(null); }}>
        <DialogContent className="max-w-2xl rounded-sm" data-testid="pick-serials-dialog">
          {pick && (
            <>
              <DialogHeader>
                <DialogTitle className="font-head text-[#0B2545]">
                  Choose serials — {pick.req.site_name}
                </DialogTitle>
                <p className="text-sm text-slate-500">
                  {pick.req.product_name} · pick exactly <b>{pick.req.qty}</b> unit(s) from the {pick.source}.
                </p>
              </DialogHeader>
              <div className="flex items-center justify-between text-xs text-slate-500 border-b border-slate-100 pb-2">
                <span>{pick.units.length} available</span>
                <span data-testid="pick-count" className={pick.sel.length === pick.req.qty ? "text-[#00857C] font-semibold" : "font-semibold"}>
                  {pick.sel.length} / {pick.req.qty} selected
                </span>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
                <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Live scan (hands-free)</div>
                <Input data-testid="pick-scan-live" value={pickScanInput} autoFocus
                  onChange={(e) => setPickScanInput(e.target.value)} onKeyDown={onPickScanEnter}
                  placeholder="Scan a serial and it auto-adds…" className="rounded-sm font-label bg-white" />
                {pickScanFeed.length > 0 && (
                  <div className="mt-2 max-h-28 overflow-y-auto border border-slate-100 bg-white rounded-sm divide-y divide-slate-50" data-testid="pick-scan-feed">
                    {pickScanFeed.map((x, i) => (
                      <div key={i} className="flex items-center justify-between px-2 py-1 text-xs">
                        <span className="font-label">{x.serial}</span>
                        <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-sm"
                          style={x.status === "ok" ? { background: "#f0fdfa", color: "#00857C" }
                            : x.status === "dup" ? { background: "#f1f5f9", color: "#475569" }
                              : x.status === "full" ? { background: "#eff6ff", color: "#1d4ed8" }
                                : { background: "#fff7ed", color: "#b45309" }}>
                          {x.status === "ok" ? "Added" : x.status === "dup" ? "Already added" : x.status === "full" ? "Limit reached" : "Not available"}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="border-t border-slate-200 mt-3 pt-2">
                  <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Or paste a list</div>
                  <Textarea data-testid="pick-scan-text" value={pickScan} onChange={(e) => setPickScan(e.target.value)} rows={2}
                    placeholder={"Paste serials (comma or newline) to auto-select…"} className="rounded-sm font-label text-sm bg-white" />
                  <div className="flex items-center gap-2 mt-2">
                    <Button data-testid="pick-scan-apply" size="sm" onClick={applyPickScan} className="rounded-sm bg-[#0B2545] hover:bg-[#0B2545]/90 font-head h-7 text-xs gap-1">
                      <ScanLine className="h-3.5 w-3.5" /> Match &amp; select
                    </Button>
                    {pickScan && <Button size="sm" variant="outline" className="rounded-sm border-slate-300 font-head h-7 text-xs" onClick={() => { setPickScan(""); setPickScanMissing([]); }}>Reset</Button>}
                    {pickScanMissing.length > 0 && <span className="text-[11px] text-[#b45309]" data-testid="pick-scan-missing">{pickScanMissing.length} not available here</span>}
                  </div>
                </div>
              </div>
              <div className="max-h-80 overflow-y-auto thin-scroll -mx-1 px-1">
                {pick.units.length === 0 ? (
                  <Empty>No units available at the {pick.source}. Top up the source first.</Empty>
                ) : (
                  <table className="w-full text-sm" data-testid="pick-units-table">
                    <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200 sticky top-0 bg-white">
                      <th className="py-2 pr-3 w-8"></th><th className="py-2 pr-3">Serial</th><th className="py-2 pr-3">Current location</th></tr></thead>
                    <tbody>
                      {pick.units.map((u) => {
                        const checked = pick.sel.includes(u.serial);
                        const atLimit = pick.sel.length >= pick.req.qty && !checked;
                        return (
                          <tr key={u.id} className={`border-b border-slate-50 ${atLimit ? "opacity-40" : ""}`}>
                            <td className="py-2 pr-3">
                              <input type="checkbox" data-testid={`pick-chk-${u.serial}`} checked={checked} disabled={atLimit} onChange={() => togglePick(u.serial)} />
                            </td>
                            <td className="py-2 pr-3 font-label text-[13px]">{u.serial}</td>
                            <td className="py-2 pr-3 text-slate-500">{u.location_name}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-sm border-slate-300 font-head" onClick={() => setPick(null)}>Cancel</Button>
                <Button data-testid="pick-confirm" onClick={confirmPick} disabled={pick.sel.length !== pick.req.qty}
                  className="rounded-sm bg-[#00857C] hover:bg-[#00a89d] font-head">
                  Dispatch {pick.sel.length}/{pick.req.qty}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
